import { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/services';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

const LOG_LEVELS = [
	{ value: '', label: 'Tất cả' },
	{ value: 'trace', label: 'Trace' },
	{ value: 'debug', label: 'Debug' },
	{ value: 'info', label: 'Info' },
	{ value: 'warn', label: 'Warn' },
	{ value: 'error', label: 'Error' },
	{ value: 'fatal', label: 'Fatal' }
];

const levelColor = (level: string) => {
	if (level === 'error' || level === 'fatal') return 'error';
	if (level === 'warn') return 'warning';
	if (level === 'debug' || level === 'trace') return 'default';
	return 'success';
};

const toIsoFilterValue = (value: string) => {
	if (!value) return undefined;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

export const BackendLogsCard = () => {
	const [keyword, setKeyword] = useState('');
	const [appliedKeyword, setAppliedKeyword] = useState('');
	const [level, setLevel] = useState('');
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(50);

	const query = useMemo(() => ({
		q: appliedKeyword,
		level,
		from: toIsoFilterValue(from),
		to: toIsoFilterValue(to),
		page,
		limit
	}), [appliedKeyword, level, from, to, page, limit]);

	const { data, isFetching, error, refetch } = useQuery({
		queryKey: ['admin', 'backend-logs', query],
		queryFn: () => adminApi.getBackendLogs(query),
		refetchOnWindowFocus: false,
		staleTime: 10000
	});

	const handleSearch = () => {
		setPage(1);
		setAppliedKeyword(keyword.trim());
	};

	const handleClear = () => {
		setKeyword('');
		setAppliedKeyword('');
		setLevel('');
		setFrom('');
		setTo('');
		setPage(1);
	};

	return (
		<Card sx={{ height: '100%' }}>
			<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6">Backend logs</Typography>
						<Typography variant="body2" color="text.secondary">
							Lưu file log theo ngày, giữ tối đa 7 ngày. Tìm kiếm và phân trang được xử lý ở server.
						</Typography>
					</Box>
					<Button
						variant="outlined"
						startIcon={isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
						onClick={() => refetch()}
						disabled={isFetching}
					>
						Làm mới
					</Button>
				</Stack>

				<Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
					<TextField
						label="Tìm theo từ khóa"
						value={keyword}
						onChange={(event) => setKeyword(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') handleSearch();
						}}
						size="small"
						fullWidth
					/>
					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Mức log</InputLabel>
						<Select
							value={level}
							label="Mức log"
							onChange={(event) => {
								setPage(1);
								setLevel(event.target.value);
							}}
						>
							{LOG_LEVELS.map(item => (
								<MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Từ ngày"
						type="datetime-local"
						value={from}
						onChange={(event) => {
							setPage(1);
							setFrom(event.target.value);
						}}
						size="small"
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="Đến ngày"
						type="datetime-local"
						value={to}
						onChange={(event) => {
							setPage(1);
							setTo(event.target.value);
						}}
						size="small"
						InputLabelProps={{ shrink: true }}
					/>
					<FormControl size="small" sx={{ minWidth: 120 }}>
						<InputLabel>Số dòng</InputLabel>
						<Select
							value={String(limit)}
							label="Số dòng"
							onChange={(event) => {
								setPage(1);
								setLimit(Number(event.target.value));
							}}
						>
							{[25, 50, 100].map(value => (
								<MenuItem key={value} value={value}>{value}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
						Tìm
					</Button>
					<Button variant="text" onClick={handleClear}>
						Xóa lọc
					</Button>
				</Stack>

				{error && (
					<Alert severity="error">
						Không thể tải backend logs.
					</Alert>
				)}

				<Box
					sx={{
						border: 1,
						borderColor: 'divider',
						borderRadius: 1,
						bgcolor: '#0f172a',
						color: '#e5e7eb',
						fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
						fontSize: 13,
						lineHeight: 1.6,
						minHeight: 420,
						maxHeight: 560,
						overflow: 'auto'
					}}
				>
					{isFetching && !data ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
							<CircularProgress />
						</Box>
					) : data?.data.length ? (
						data.data.map((log, index) => (
							<Box
								key={`${log.time ?? 'no-time'}-${index}`}
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: '170px 76px minmax(0, 1fr)' },
									gap: 1,
									px: 1.5,
									py: 1,
									borderBottom: 1,
									borderColor: 'rgba(148, 163, 184, 0.2)'
								}}
							>
								<Typography component="span" sx={{ color: '#93c5fd', fontFamily: 'inherit', fontSize: 'inherit' }}>
									{log.time ?? '-'}
								</Typography>
								<Box>
									<Chip
										label={log.level}
										size="small"
										color={levelColor(log.level)}
										sx={{ height: 22, textTransform: 'uppercase', fontSize: 11 }}
									/>
								</Box>
								<Typography
									component="pre"
									sx={{
										m: 0,
										whiteSpace: 'pre-wrap',
										wordBreak: 'break-word',
										fontFamily: 'inherit',
										fontSize: 'inherit'
									}}
								>
									{log.raw}
								</Typography>
							</Box>
						))
					) : (
						<Typography sx={{ p: 3, color: '#cbd5e1' }}>
							Không có log phù hợp.
						</Typography>
					)}
				</Box>

				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
					<Typography variant="body2" color="text.secondary">
						{data ? `${data.pagination.total.toLocaleString()} dòng, trang ${data.pagination.page}/${data.pagination.totalPages || 1}` : 'Đang tải logs'}
					</Typography>
					<Pagination
						count={data?.pagination.totalPages || 1}
						page={page}
						onChange={(_, value) => setPage(value)}
						color="primary"
						shape="rounded"
					/>
				</Stack>
			</CardContent>
		</Card>
	);
};
